import { EASING_BEZIER } from 'app/config';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MigrationArrows } from './MigrationArrows';
import { ReactComponent as MigrationIconStart } from 'common/assets/jb_blue.svg';
import { ReactComponent as MigrationIconEnd } from 'common/assets/jb_one.svg';
import { InternalNavigationScreens, ServiceProvider } from 'features/entities/entitiesTypes';
import { jbPalette } from '@el2/theme';
import { useDispatch } from 'react-redux';
import { openExternalServiceStart } from 'features/extNavigation/extNavigationSlice';
import { fetchMigrationDataRequest } from 'features/entities/entitiesRequests';
import { Dispatch } from 'redux';
import { getMapBridge } from 'features/map';
import { isMigrationInstance } from 'common/utils/migrationUtils';
import { screenToRoutesMap } from 'features/routes/appRoutes';
import { MigrationBackground } from './MigrationBackground';

const fetchMigrationUrlOnLoad = async (dispatch: Dispatch) => {
  const migrationData = await fetchMigrationDataRequest();
  dispatch(
    openExternalServiceStart({
      code: migrationData.code,
      url: migrationData.url,
    } as ServiceProvider)
  );
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && isMigrationInstance()) {
    getMapBridge().closePage();
  }
};

export const MigrationInterimView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isTabletWidth = useMediaQuery('(min-width:481px)');
  const dispatch = useDispatch();

  const handleOpen = useCallback(async () => {
    try {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      await fetchMigrationUrlOnLoad(dispatch);
    } catch (error) {
      console.error(error);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      navigate(screenToRoutesMap[InternalNavigationScreens.MIGRATION_ERROR_SCREEN]);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(handleOpen, 50);
    return () => clearTimeout(timeoutId);
  }, [handleOpen]);

  return (
    <motion.div
      style={{
        width: '100%',
        flex: 1,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',

        alignItems: 'center',
      }}
      initial={{ x: window.innerWidth }}
      animate={{ x: 0 }}
      exit={{ x: window.innerWidth }}
      transition={{ ease: EASING_BEZIER, duration: 0.2 }}
    >
      <MigrationBackground />
      <Box
        sx={{
          maxWidth: '554px', // 506 + 2*24px padding
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          p: 3,
          zIndex: 1,
          overflow: 'hidden',
          '@container (min-height: 744px)': {
            pt: 8,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            gap: 3,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2.5,
              height: 122,
            }}
            className="migrationGraphics"
          >
            <MigrationIconStart />
            <MigrationArrows
              sx={{
                position: 'relative',
                // the migration end icon has some extra white space on
                // the left because of the shadow of the svg
                // so we move the arrows to visually align
                left: 4,
                fontSize: 59,
              }}
            />
            <Box
              sx={{
                width: 114,
                aspectRatio: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <MigrationIconEnd
                style={{
                  flex: '0 0 126px',
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingBottom: theme.spacing(2),
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: 'center',
                color: jbPalette.primary.royalBlue900,
                fontSize: (theme) => theme.typography.pxToRem(20),
                lineHeight: '26px',
                width: 200,
                ...(isTabletWidth && {
                  fontSize: (theme) => theme.typography.pxToRem(24),
                  lineHeight: '28px',
                  width: 240,
                }),
              }}
            >
              {t('migration.step.interim.name')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};
